import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  Flex,
  IconButton,
  Portal,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
} from '@chakra-ui/react';
import useBirthdayPicker from '../../../../../hooks/useBirthdayPicker';
import { BIRTHDAY_FORMAT } from '../../../../../shop/constants/user';
import {
  getJsMonth,
  getFormattedBirthday,
  validateBirthday,
} from '../../../../../utils/birthday';
import { useAppContext } from '../../../context/appContext';
import FormField from './FormField';

const BIRTHDAY_KEY = {
  year: 'year',
  month: 'month',
  date: 'date',
};

const BirthdayFAQ = () => {
  const { userSetting } = useAppContext();
  const { t } = useTranslation();
  if (userSetting.birthday_format === BIRTHDAY_FORMAT.MMDD) {
    return null;
  }
  return (
    <Popover placement="right">
      <PopoverTrigger>
        <IconButton
          icon={<i className="fa fa-lg fa-question-circle birthday-faq" />}
          minHeight="auto"
          backgroundColor="transparent"
          _hover={{
            backgroundColor: 'transparent',
          }}
          _active={{
            backgroundColor: 'transparent',
          }}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          _focus={{
            boxShadow: 'none',
          }}
        >
          <PopoverBody>{t('users.birthday.faq')}</PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const BirthdayPicker = (props) => {
  const { required } = props;
  const { userSetting } = useAppContext();
  const { control, errors, getValues } = useFormContext();
  const { birthday_format: birthdayFormat, minimum_age_limit: minAge } =
    userSetting;
  const { year, month: _month, date } = getValues('birthday') || {};
  const month = getJsMonth(_month);
  const [isValidDefaultBirthday] = useState(
    validateBirthday({
      birthdayFormat,
      minAge,
      year,
      month,
      date,
    }) === null,
  );
  const { t } = useTranslation();
  const { field } = useController({
    name: 'birthday',
    control,
  });
  const {
    shouldShowDate,
    shouldShowYear,
    yearOptions,
    monthOptions,
    dateOptions,
    EMPTY_OPTION,
  } = useBirthdayPicker({
    value: field.value,
    onChange: field.onChange,
    birthdayFormat,
  });
  let label = t('membership.profile.birthday');
  if (!required) {
    label += ` ${t('membership.profile.optional_field')}`;
  }

  const selectList = [
    {
      key: BIRTHDAY_KEY.year,
      text: t('birthday_picker.year.placeholder'),
      options: yearOptions,
      shouldShow: shouldShowYear,
      e2eId: 'member-center-profile-birthday-year_dropdown',
    },
    {
      key: BIRTHDAY_KEY.month,
      text: t('birthday_picker.month.placeholder'),
      options: monthOptions,
      shouldShow: true,
      e2eId: 'member-center-profile-birthday-month_dropdown',
    },
    {
      key: BIRTHDAY_KEY.date,
      text: t('birthday_picker.date.placeholder'),
      options: dateOptions,
      shouldShow: shouldShowDate,
      e2eId: 'member-center-profile-birthday-date_dropdown',
    },
  ];

  const handleChange = (key) => (event) => {
    const value = event.target.value;
    field.onChange({
      ...field.value,
      [key]: value === EMPTY_OPTION ? null : Number(value),
    });
  };

  return (
    <FormField
      label={
        <Flex alignItems="center" gap="0.25rem">
          <Text variant="formLabel" className="member-center-profile__label">
            {label}
          </Text>
          <BirthdayFAQ />
        </Flex>
      }
      hasError={Boolean(errors.birthday)}
    >
      {isValidDefaultBirthday ? (
        <Text textAlign="left" mt="9px">
          {getFormattedBirthday(year, month, date)}
        </Text>
      ) : (
        <>
          <Flex gap="1rem">
            {selectList.map(
              ({ key, text, options, shouldShow, e2eId }) =>
                shouldShow && (
                  <Select
                    name={key}
                    key={key}
                    onChange={handleChange(key)}
                    value={field.value[key]}
                    data-e2e-id={e2eId}
                  >
                    <option hidden value={null}>
                      {text}
                    </option>
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                ),
            )}
          </Flex>
          {errors.birthday && (
            <Text variant="errorMessage" textAlign="right">
              {t(
                errors.birthday.message.translateKey,
                errors.birthday.message.params,
              )}
            </Text>
          )}
        </>
      )}
    </FormField>
  );
};

BirthdayPicker.propTypes = {
  required: PropTypes.bool,
};

export default BirthdayPicker;
